import { EPropertyType } from '@modules/project-creator/shared/enums/property-type.enum';
import { EConfiguratorQuestionType, EProjectScope, IConfiguratorSection } from '../models/configurator.model';
import {
  configuratorIcon,
  COMMERCIAL_USE_OPTIONS,
  COMMON_AREA_NONE,
  COMMON_AREA_OPTIONS,
  EMPTY_AT_START_OPTIONS,
  HOUSE_PROPERTY_TYPES,
  OWNERS_ASSOCIATION,
  OWNERSHIP_TYPE_OPTIONS,
  PARKING_SITUATION_OPTIONS,
  PROPERTY_AGE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  ROOM_HEIGHT_OPTIONS,
  UNITS_OCCUPIED_OPTIONS,
  YES_NO_UNKNOWN_OPTIONS
} from './configurator-options.data';
import { getPropertyKind, isNotScope, isPropertyKind, isScope } from './configurator-predicates';

const T = 'ProjectConfiguratorV2.Questions';
const S = 'ProjectConfiguratorV2.Sections';
const SUMMARY = 'ProjectConfiguratorV2.Summary';

/** Step 2 "Immobilie". The existing customer's object picker comes with stage 9. */
export const PROPERTY_STEP_SECTIONS: IConfiguratorSection[] = [
  {
    id: 'property',
    questions: [
      {
        id: 'propertyType',
        type: EConfiguratorQuestionType.TILES,
        summaryLabel: `${T}.propertyType.summary`,
        label: `${T}.propertyType.label`,
        options: PROPERTY_TYPE_OPTIONS,
        filterOptions: (options, answers) =>
          answers.projectScope === EProjectScope.HOUSE
            ? options.filter((option) => HOUSE_PROPERTY_TYPES.includes(option.value))
            : options,
        show: isNotScope(EProjectScope.FLAT, EProjectScope.MULTI_FAMILY, EProjectScope.COMMERCIAL)
      },
      {
        id: 'address',
        type: EConfiguratorQuestionType.ADDRESS,
        summaryLabel: `${T}.address.summary`,
        label: `${T}.address.label`,
        required: true,
        requiredMessage: `${T}.address.required`,
        answered: (_, answers) => !!answers.postalCode || !!answers.adressUnknown
      }
    ]
  },
  {
    id: 'units',
    icon: configuratorIcon('mfh'),
    title: `${S}.units`,
    show: isScope(EProjectScope.MULTI_FAMILY),
    questions: [
      {
        id: 'unitsCount',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.unitsCount.summary`,
        label: `${T}.unitsCount.label`,
        half: true,
        min: 1
      },
      {
        id: 'unitsToRenovate',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.unitsToRenovate.summary`,
        label: `${T}.unitsToRenovate.label`,
        half: true
      },
      {
        id: 'commonAreas',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.commonAreas.summary`,
        label: `${T}.commonAreas.label`,
        options: COMMON_AREA_OPTIONS,
        multiple: true,
        exclusiveValues: [COMMON_AREA_NONE],
        allowCustom: true
      },
      {
        id: 'unitsOccupied',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.unitsOccupied.summary`,
        label: `${T}.unitsOccupied.label`,
        options: UNITS_OCCUPIED_OPTIONS
      }
    ]
  },
  {
    id: 'commercial',
    icon: configuratorIcon('shop'),
    title: `${S}.commercial`,
    show: isScope(EProjectScope.COMMERCIAL),
    questions: [
      {
        id: 'commercialUse',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.commercialUse.summary`,
        label: `${T}.commercialUse.label`,
        options: COMMERCIAL_USE_OPTIONS,
        allowCustom: true
      },
      {
        id: 'businessOperating',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.businessOperating.summary`,
        label: `${T}.businessOperating.label`,
        options: YES_NO_UNKNOWN_OPTIONS
      }
    ]
  },
  {
    id: 'ageAndSize',
    icon: configuratorIcon('layers'),
    title: `${S}.ageAndSize`,
    questions: [
      {
        id: 'propertyAge',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.propertyAge.label`,
        help: `${T}.propertyAge.help`,
        options: PROPERTY_AGE_OPTIONS,
        allowCustom: true,
        customLabel: `${T}.propertyAge.customLabel`,
        customPlaceholder: `${T}.propertyAge.customPlaceholder`,
        customInputMode: 'numeric'
      },
      {
        id: 'area',
        type: EConfiguratorQuestionType.NUMBER,
        label: `${T}.area.label`,
        suffix: 'm²',
        step: 5,
        seed: 60,
        half: true
      },
      {
        id: 'renovationArea',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.renovationArea.summary`,
        label: `${T}.renovationArea.label`,
        suffix: 'm²',
        step: 5,
        half: true,
        show: isNotScope(EProjectScope.BATHROOM, EProjectScope.KITCHEN)
      },
      {
        id: 'balconyArea',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.balconyArea.summary`,
        label: `${T}.balconyArea.label`,
        suffix: 'm²',
        half: true,
        show: isScope(EProjectScope.FLAT, EProjectScope.HOUSE)
      },
      {
        id: 'lastRenovationNote',
        type: EConfiguratorQuestionType.TEXT,
        label: `${T}.lastRenovationNote.label`,
        placeholder: `${T}.lastRenovationNote.placeholder`,
        maxLength: 200,
        half: true
      },
      {
        id: 'roomHeight',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.roomHeight.summary`,
        label: `${T}.roomHeight.label`,
        options: ROOM_HEIGHT_OPTIONS,
        allowCustom: true,
        show: isNotScope(EProjectScope.EXTERIOR)
      }
    ]
  },
  {
    id: 'floors',
    icon: configuratorIcon('mfh'),
    title: `${S}.floors`,
    show: (answers) => !!getPropertyKind(answers),
    questions: [
      {
        id: 'floor',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.floor.summary`,
        summaryValue: (value, { translate }) => {
          const floor = Number(value);
          if (floor === 0) return translate(`${SUMMARY}.groundFloor`);
          return floor < 0
            ? translate(`${SUMMARY}.basementFloor`)
            : translate(`${SUMMARY}.upperFloor`, { floor });
        },
        label: `${T}.floor.label`,
        help: `${T}.floor.help`,
        suffix: 'ProjectConfiguratorV2.Units.floor',
        min: -1,
        half: true,
        show: isPropertyKind(EPropertyType.flat)
      },
      {
        id: 'floorsCount',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.floorsCount.summary`,
        label: `${T}.floorsCount.label`,
        help: `${T}.floorsCount.help`,
        min: 1,
        half: true
      },
      {
        id: 'hasElevator',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.hasElevator.summary`,
        label: `${T}.hasElevator.label`,
        options: YES_NO_UNKNOWN_OPTIONS,
        show: isPropertyKind(EPropertyType.flat, EPropertyType.townhouse, EPropertyType.commercial)
      }
    ]
  },
  {
    id: 'conditions',
    icon: configuratorIcon('pin'),
    title: `${S}.conditions`,
    questions: [
      {
        id: 'hasBasement',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.hasBasement.label`,
        options: YES_NO_UNKNOWN_OPTIONS
      },
      {
        id: 'parkingSituation',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.parkingSituation.label`,
        options: PARKING_SITUATION_OPTIONS
      },
      {
        id: 'ownershipType',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.ownershipType.label`,
        options: OWNERSHIP_TYPE_OPTIONS,
        allowCustom: true
      },
      {
        id: 'propertyManagement',
        type: EConfiguratorQuestionType.TEXT,
        label: `${T}.propertyManagement.label`,
        placeholder: `${T}.propertyManagement.placeholder`,
        maxLength: 200,
        show: (answers) => answers.ownershipType === OWNERS_ASSOCIATION
      },
      {
        id: 'occupiedDuringWorks',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.occupiedDuringWorks.label`,
        options: YES_NO_UNKNOWN_OPTIONS,
        show: isNotScope(EProjectScope.MULTI_FAMILY)
      },
      {
        id: 'emptyAtStart',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.emptyAtStart.summary`,
        label: `${T}.emptyAtStart.label`,
        options: EMPTY_AT_START_OPTIONS,
        show: isNotScope(EProjectScope.EXTERIOR)
      },
      {
        id: 'listedBuilding',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.listedBuilding.label`,
        options: YES_NO_UNKNOWN_OPTIONS
      }
    ]
  }
];
